// ============================================================================
// AFB-0075 — place a template's fields ONCE, on the template's own PDF.
//
// WHY THIS EXISTS, given that auto-placement shipped 2026-08-20.
// "Place fields from this PDF's form" reads the widgets a PDF declares about
// itself. Measured against the stored bytes on 2026-08-25, the two documents
// that matter most in the W2 onboarding packet declare NONE:
//
//   TPL-0006  Employee handbook (15pp, checklist key handbook_signed)  0 widgets
//   TPL-0005  Employee personal information form (2pp)                 0 widgets
//
// Neither has an /AcroForm dictionary or a single widget annotation. They are
// flat documents. So for the handbook — the one document whose signature IS the
// onboarding checklist item — auto-placement can never produce a box, and until
// this screen existed there was no way to put one there except to create a
// throwaway envelope, drag boxes onto it on the prep screen, and use "Save
// layout" to copy them back. That round trip is why seventeen templates and two
// packets produced zero envelopes in the five days after the feature shipped.
//
// WHAT IS DELIBERATELY NOT HERE.
//   · No coordinate arithmetic. Every number comes from ./field-canvas, which a
//     node test can load. This file asks for them and renders the answer, the
//     same division template-authoring keeps with ./acroform-placement.
//   · No save. The parent owns the draft and the one save path, so a field
//     placed here and a field tagged in the list below cannot disagree about
//     what is stored.
//   · No tagging rules. field_class / binding / is_sensitive all go through
//     normaliseFieldTagging and markFieldRestricted, so "we prefill the
//     employee's SSN" stays a state this system will not represent.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext, DragEndEvent, PointerSensor, useDraggable, useSensor, useSensors,
} from '@dnd-kit/core';
import { Loader2, Trash2 } from 'lucide-react';
import {
  EsignTemplate, FIELD_DEFAULTS, FieldBindingOption, FieldType, RenderedPage,
  TemplateField, TemplateFieldClass, base64ToBytes, loadTemplateDocumentBase64,
  markFieldRestricted, normaliseFieldTagging, recipientColor, renderPdfPages,
} from './envelope-lib';
import {
  DEFAULT_ROLE_KEY, PageFrame, ResizeHandle, boxAtPoint, fieldsPerPage,
  moveBoxToPoint, placementBlockers, resizeBox,
} from './field-canvas';
import { FIELD_LABEL } from './signer-field';
import { errorMessage } from '../../shared/errors';

const FIELD_TYPES: FieldType[] = [
  'signature', 'initials', 'name', 'email', 'date', 'text', 'checkbox',
];

const HANDLES: ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/** Where each handle sits on the box, and which cursor it should show. */
const HANDLE_STYLE: Record<ResizeHandle, { css: React.CSSProperties; cursor: string }> = {
  nw: { css: { left: -4, top: -4 }, cursor: 'nwse-resize' },
  n: { css: { left: 'calc(50% - 4px)', top: -4 }, cursor: 'ns-resize' },
  ne: { css: { right: -4, top: -4 }, cursor: 'nesw-resize' },
  e: { css: { right: -4, top: 'calc(50% - 4px)' }, cursor: 'ew-resize' },
  se: { css: { right: -4, bottom: -4 }, cursor: 'nwse-resize' },
  s: { css: { left: 'calc(50% - 4px)', bottom: -4 }, cursor: 'ns-resize' },
  sw: { css: { left: -4, bottom: -4 }, cursor: 'nesw-resize' },
  w: { css: { left: -4, top: 'calc(50% - 4px)' }, cursor: 'ew-resize' },
};

const INPUT =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs text-tcs-text';
const LABEL = 'block text-[11px] font-medium text-tcs-text-secondary';
const SECONDARY =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-tcs-border px-2 text-[11px] text-tcs-text-secondary disabled:opacity-50';

/**
 * The role colour for a box, or null when it points at no role on this
 * template.
 *
 * Null is not a styling detail. A box whose role_key was renamed or deleted is
 * an ORPHAN: nobody will be asked to fill it, and instantiation only reports
 * that at send time, after the envelope row already exists. So it is drawn in
 * the warning vocabulary the blockers panel below already uses rather than in a
 * role colour it has no right to — colouring it like a real signer's box would
 * hide the one thing worth saying about it.
 */
function colourForRole(template: EsignTemplate, roleKey: string | null): string | null {
  const roles = Array.isArray(template.recipient_roles) ? template.recipient_roles : [];
  const wanted = (roleKey ?? '').trim() || DEFAULT_ROLE_KEY;
  const i = roles.findIndex((r) => r.role_key === wanted);
  return i < 0 ? null : recipientColor(i);
}

const ORPHAN_BOX =
  'border-st-warn-border-strong bg-st-warn-bg text-st-warn-fg-strong';

// ── palette chip ────────────────────────────────────────────────────────────

function PaletteChip({ type, disabled }: { type: FieldType; disabled: boolean }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `tplpalette-${type}`,
    disabled,
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      disabled={disabled}
      {...listeners}
      {...attributes}
      className="cursor-grab touch-none rounded-md border border-tcs-border bg-tcs-raised px-2.5 py-1.5 text-[11px] font-medium text-tcs-text active:cursor-grabbing disabled:opacity-50"
      style={{
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
        zIndex: transform ? 50 : undefined,
        position: 'relative',
      }}
    >
      {FIELD_LABEL[type]}
    </button>
  );
}

// ── one placed box ──────────────────────────────────────────────────────────

function CanvasBox({
  field, index, colour, pageW, pageH, selected, disabled, onSelect, onResize,
}: {
  field: TemplateField;
  index: number;
  /** null ⇒ this box points at no role on the template. Drawn as a warning. */
  colour: string | null;
  pageW: number;
  pageH: number;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onResize: (handle: ResizeHandle, dx: number, dy: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `tplfield-${index}`,
    disabled,
  });
  const tagging = normaliseFieldTagging(field);

  /**
   * Resize lives on raw pointer events rather than dnd-kit.
   *
   * stopPropagation on pointerdown is load-bearing: without it dnd-kit's
   * PointerSensor claims the gesture and the box slides across the page instead
   * of growing, which reads as the handle being broken.
   */
  function startResize(handle: ResizeHandle, ev: React.PointerEvent) {
    if (disabled) return;
    ev.preventDefault();
    ev.stopPropagation();
    const startX = ev.clientX;
    const startY = ev.clientY;
    const target = ev.currentTarget as HTMLElement;
    target.setPointerCapture(ev.pointerId);
    let last = { dx: 0, dy: 0 };
    const move = (e: PointerEvent) => {
      last = { dx: (e.clientX - startX) / pageW, dy: (e.clientY - startY) / pageH };
      onResize(handle, last.dx, last.dy);
    };
    const up = () => {
      target.removeEventListener('pointermove', move);
      target.removeEventListener('pointerup', up);
      target.removeEventListener('pointercancel', up);
    };
    target.addEventListener('pointermove', move);
    target.addEventListener('pointerup', up);
    target.addEventListener('pointercancel', up);
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      role="button"
      tabIndex={0}
      aria-label={`${FIELD_LABEL[field.field_type]} on page ${field.page}, field ${index + 1}`}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
      }}
      className={
        'absolute flex items-center justify-center overflow-visible rounded-sm border '
        + `text-[10px] font-medium ${colour === null ? ORPHAN_BOX : ''}`
      }
      style={{
        left: field.position_x * pageW,
        top: field.position_y * pageH,
        width: field.width * pageW,
        height: field.height * pageH,
        borderColor: colour ?? undefined,
        backgroundColor: colour ? `${colour}22` : undefined,
        color: colour ?? undefined,
        outline: selected && colour ? `2px solid ${colour}` : undefined,
        outlineStyle: selected && !colour ? 'dashed' : undefined,
        outlineWidth: selected && !colour ? 2 : undefined,
        transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
        cursor: disabled ? 'default' : 'move',
        touchAction: 'none',
      }}
    >
      <span className="truncate px-0.5">
        {FIELD_LABEL[field.field_type]}
        {field.required !== false && <span className="font-bold text-st-danger-fg"> *</span>}
        {tagging.is_sensitive && <span className="font-semibold"> · restricted</span>}
      </span>
      {selected && !disabled && HANDLES.map((h) => (
        <span
          key={h}
          onPointerDown={(e) => startResize(h, e)}
          className={
            'absolute h-2 w-2 rounded-sm border border-tcs-border '
            + (colour === null ? 'bg-st-warn-fg-strong' : '')
          }
          style={{
            ...HANDLE_STYLE[h].css,
            backgroundColor: colour ?? undefined,
            cursor: HANDLE_STYLE[h].cursor,
          }}
        />
      ))}
    </div>
  );
}

// ── page shell ──────────────────────────────────────────────────────────────

function CanvasPage({
  page, children, registerRef,
}: {
  page: RenderedPage;
  children: React.ReactNode;
  registerRef: (n: number, el: HTMLDivElement | null) => void;
}) {
  const holder = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = holder.current;
    if (el && page.canvas.parentElement !== el) {
      el.innerHTML = '';
      el.appendChild(page.canvas);
    }
  }, [page]);
  return (
    <div className="mb-4">
      <p className="mb-1 text-[11px] font-medium text-tcs-text-secondary">
        Page {page.pageNumber}
      </p>
      <div
        ref={(el) => registerRef(page.pageNumber, el)}
        data-page={page.pageNumber}
        className="relative mx-auto border border-tcs-border bg-tcs-raised shadow-sm"
        style={{ width: page.width, height: page.height }}
      >
        <div ref={holder} aria-hidden />
        {children}
      </div>
    </div>
  );
}

// ── main ────────────────────────────────────────────────────────────────────

export interface TemplateFieldCanvasProps {
  template: EsignTemplate;
  /** The DRAFT layout the parent holds. This component never saves. */
  layout: TemplateField[];
  onChange: (layout: TemplateField[]) => void;
  /** Already filtered through selectableBindings by the parent. */
  bindings: FieldBindingOption[];
  disabled: boolean;
}

export function TemplateFieldCanvas({
  template, layout, onChange, bindings, disabled,
}: TemplateFieldCanvasProps) {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const roles = Array.isArray(template.recipient_roles) ? template.recipient_roles : [];
  const signers = roles.filter((r) => (r.role ?? 'SIGNER').toUpperCase() === 'SIGNER');
  const blockers = placementBlockers(layout, {
    page_count: template.page_count,
    recipient_roles: roles,
  });
  const perPage = fieldsPerPage(layout, template.page_count);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const registerRef = useCallback((n: number, el: HTMLDivElement | null) => {
    if (el) pageRefs.current.set(n, el);
    else pageRefs.current.delete(n);
  }, []);

  /**
   * Render the template's own PDF, on demand.
   *
   * Behind a button rather than on mount: the handbook is fifteen pages and the
   * Missouri W-4 is 1.1 MB, and this editor sits inside a list where every
   * template's panel could otherwise start decoding a PDF at once.
   *
   * `.buffer` is load-bearing — renderPdfPages takes an ArrayBuffer, and pdf.js
   * TRANSFERS the buffer it is handed to its worker, so this copy must not be
   * shared with anything else that wants to read the same bytes.
   */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const b64 = await loadTemplateDocumentBase64(template);
        const bytes = base64ToBytes(b64);
        const rendered = await renderPdfPages(
          bytes.buffer.slice(0) as ArrayBuffer,
          Math.min(760, window.innerWidth - 48),
        );
        if (!cancelled) setPages(rendered);
      } catch (e) {
        if (!cancelled) setLoadError(errorMessage(e, 'This template PDF could not be opened.'));
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, template]);

  // A save replaces the template object, which resets the parent's draft. The
  // selection has to go with it or the inspector edits a field that moved.
  useEffect(() => { setSelected(null); }, [template]);

  /** Which rendered page a viewport point is inside, and where that page is. */
  function pageAtPoint(x: number, y: number): { n: number; frame: PageFrame } | null {
    for (const [n, el] of pageRefs.current.entries()) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
        const page = pages.find((p) => p.pageNumber === n);
        if (!page) return null;
        return { n, frame: { left: r.left, top: r.top, width: page.width, height: page.height } };
      }
    }
    return null;
  }

  function patch(index: number, next: Partial<TemplateField>) {
    onChange(layout.map((f, i) => (i === index ? { ...f, ...next } : f)));
  }

  function onDragEnd(ev: DragEndEvent) {
    const id = String(ev.active.id);
    const box = ev.active.rect.current.translated;
    if (!box || disabled) return;
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    const hit = pageAtPoint(cx, cy);
    if (!hit) return;

    if (id.startsWith('tplpalette-')) {
      const type = id.slice('tplpalette-'.length) as FieldType;
      const def = FIELD_DEFAULTS[type];
      const placed = boxAtPoint(cx, cy, hit.frame, def.w, def.h);
      const roleIndex = roles.findIndex((r) => r.role_key === (signers[0]?.role_key ?? ''));
      onChange([
        ...layout,
        {
          recipient_index: roleIndex < 0 ? 0 : roleIndex,
          role_key: signers[0]?.role_key ?? 'employee',
          field_type: type,
          page: hit.n,
          ...placed,
          required: true,
          field_class: 'signer',
          binding: null,
          is_sensitive: false,
        },
      ]);
      setSelected(layout.length);
      return;
    }

    if (id.startsWith('tplfield-')) {
      const index = Number(id.slice('tplfield-'.length));
      const f = layout[index];
      if (!f) return;
      patch(index, { page: hit.n, ...moveBoxToPoint(f, cx, cy, hit.frame) });
      setSelected(index);
    }
  }

  const active = selected !== null ? layout[selected] : undefined;
  const activeTagging = active ? normaliseFieldTagging(active) : null;

  return (
    <div className="mt-1.5 rounded-md border border-tcs-border bg-tcs-bg p-2">
      <p className="text-[11px] text-tcs-text-muted">
        Put the boxes on the document itself. Drag a box from the row below onto
        the page, drag it again to move it, and pull a corner to resize.
        <strong> Placed once here, every packet built from this template carries
        them</strong> — nobody has to do it per person.
      </p>

      {!open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className={`mt-1.5 ${SECONDARY}`}
        >
          Open the document and place fields
          {template.page_count ? ` · ${template.page_count} pages` : ''}
        </button>
      ) : (
        <>
          {signers.length === 0 && (
            <p role="alert" className="mt-1.5 text-[11px] text-st-warn-fg">
              Add a signer under &ldquo;Who signs it&rdquo; first and save it.
              A box has to belong to somebody or nobody is asked to fill it.
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-medium text-tcs-text-secondary">Drag on:</span>
            {FIELD_TYPES.map((t) => (
              <PaletteChip key={t} type={t} disabled={disabled || signers.length === 0} />
            ))}
          </div>

          {/* Which pages still have nothing on them. A silently shorter list is
              a list the author cannot trust, so every page is named. */}
          {perPage.length > 0 && (
            <p className="mt-1.5 text-[11px] text-tcs-text-muted">
              {perPage.map((p) => `p${p.page}: ${p.count}`).join(' · ')}
            </p>
          )}

          {loadError && (
            <p role="alert" className="mt-1.5 text-[11px] text-st-danger-fg">{loadError}</p>
          )}
          {loading && (
            <p className="mt-2 flex items-center gap-1.5 text-[11px] text-tcs-text-secondary">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Opening the document…
            </p>
          )}

          {active && activeTagging && (
            <div className="mt-2 rounded-md border border-tcs-border bg-tcs-raised p-2">
              <p className="text-[11px] font-semibold text-tcs-text">
                Field {(selected ?? 0) + 1} — {FIELD_LABEL[active.field_type]} on page {active.page}
              </p>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                <div>
                  <label className={LABEL} htmlFor="canvas-role">Belongs to</label>
                  <select
                    id="canvas-role"
                    value={active.role_key ?? ''}
                    disabled={disabled}
                    onChange={(e) => patch(selected as number, {
                      role_key: e.target.value === '' ? null : e.target.value,
                      recipient_index: Math.max(
                        0, roles.findIndex((r) => r.role_key === e.target.value),
                      ),
                    })}
                    className={INPUT}
                  >
                    {roles.map((r) => (
                      <option key={r.role_key} value={r.role_key}>{r.label || r.role_key}</option>
                    ))}
                    {/* A role that was renamed out from under this field. Shown
                        so a save of some other field cannot quietly blank it —
                        the list below already names it as a problem. */}
                    {active.role_key && !roles.some((r) => r.role_key === active.role_key) && (
                      <option value={active.role_key}>{active.role_key} (no such role)</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className={LABEL} htmlFor="canvas-class">Who fills it</label>
                  <select
                    id="canvas-class"
                    value={activeTagging.field_class}
                    disabled={disabled || activeTagging.is_sensitive}
                    onChange={(e) => patch(selected as number, {
                      ...normaliseFieldTagging({
                        ...active, field_class: e.target.value as TemplateFieldClass,
                      }),
                    })}
                    className={INPUT}
                  >
                    <option value="signer">Signer fills it</option>
                    {!activeTagging.is_sensitive && <option value="merge">We prefill it</option>}
                  </select>
                </div>
                {activeTagging.field_class === 'merge' && !activeTagging.is_sensitive && (
                  <div className="col-span-2">
                    <label className={LABEL} htmlFor="canvas-binding">Prefill from</label>
                    <select
                      id="canvas-binding"
                      value={activeTagging.binding ?? ''}
                      disabled={disabled || bindings.length === 0}
                      onChange={(e) => patch(selected as number, {
                        binding: e.target.value === '' ? null : e.target.value,
                      })}
                      className={INPUT}
                    >
                      <option value="">Choose a value…</option>
                      {bindings.map((b) => (
                        <option key={b.binding_key} value={b.binding_key}>{b.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <label className="mt-1.5 flex items-center gap-2 text-[11px] text-tcs-text">
                <input
                  type="checkbox"
                  checked={active.required !== false}
                  disabled={disabled}
                  onChange={(e) => patch(selected as number, { required: e.target.checked })}
                  className="h-4 w-4"
                />
                Must be filled before it can be finished
              </label>
              {/* THE MANUAL RESTRICTED TICK, on the canvas as well as the list.
                  The account-number box on the real Direct Deposit form is named
                  "undefined" in the PDF, so it is placed by hand — here — and
                  this is the only place it can be marked. */}
              <label className="mt-1.5 flex items-start gap-2 text-[11px] text-tcs-text">
                <input
                  type="checkbox"
                  checked={activeTagging.is_sensitive}
                  disabled={disabled}
                  onChange={(e) => patch(selected as number, {
                    ...markFieldRestricted(active, e.target.checked),
                  })}
                  className="mt-0.5 h-4 w-4 shrink-0"
                />
                <span>
                  <span className="font-medium">
                    Restricted — only HR and executives can see the answer
                  </span>
                  <span className="block text-tcs-text-muted">
                    For a Social Security number, date of birth, or bank account
                    and routing number. The employee types it in themselves.
                  </span>
                </span>
              </label>
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  onChange(layout.filter((_, i) => i !== selected));
                  setSelected(null);
                }}
                className={`mt-1.5 ${SECONDARY}`}
              >
                <Trash2 className="h-3 w-3" aria-hidden /> Remove this field
              </button>
            </div>
          )}

          {blockers.length > 0 && (
            <div className="mt-2 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-2">
              <p className="text-[11px] font-semibold text-st-warn-fg-strong">
                {blockers.length} field{blockers.length === 1 ? '' : 's'} could not be placed
              </p>
              <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-[11px] text-st-warn-fg-strong">
                {blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}

          <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            <div className="mt-2 max-h-[70vh] overflow-y-auto">
              {pages.map((p) => (
                <CanvasPage key={p.pageNumber} page={p} registerRef={registerRef}>
                  {layout.map((f, i) => (f.page === p.pageNumber ? (
                    <CanvasBox
                      key={`f-${i}`}
                      field={f}
                      index={i}
                      colour={colourForRole(template, f.role_key ?? null)}
                      pageW={p.width}
                      pageH={p.height}
                      selected={selected === i}
                      disabled={disabled}
                      onSelect={() => setSelected(i)}
                      onResize={(h, dx, dy) => patch(i, resizeBox(f, h, dx, dy))}
                    />
                  ) : null))}
                </CanvasPage>
              ))}
            </div>
          </DndContext>

          <button
            type="button"
            onClick={() => { setOpen(false); setPages([]); setSelected(null); }}
            className={`mt-1.5 ${SECONDARY}`}
          >
            Close the document
          </button>
        </>
      )}
    </div>
  );
}
